/**
 * Профиль: данные аккаунта и переходы в разделы.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useProfile } from '@/entities/subscription/queries';
import { useRaffles } from '@/features/raffle/raffles';
import { spinsWord, useWheel } from '@/features/wheel/wheel';
import { apiGet } from '@/shared/api/client';
import { paymentHistorySchema, type PaymentHistory } from '@/shared/api/schemas';
import { formatDate, formatDays, formatRub } from '@/shared/lib/format';
import { ticketWord } from '@/shared/lib/tickets';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Screen } from '@/shared/ui/Screen';
import { Card, GroupCaption } from '@/shared/ui/kit/Card';
import { Row } from '@/shared/ui/kit/Row';
import { Sheet } from '@/shared/ui/kit/Sheet';

import styles from './ProfilePage.module.css';

/** Срок или пакет: «VPN, 3 месяца», «Подарок, 1 месяц», «25 ГБ «Белый список»». */
function title(item: PaymentHistory['items'][number]): string {
  if (item.cdn_pack_gb) return `${item.cdn_pack_gb} ГБ «Белый список»`;
  const days = item.duration_days;
  let term = 'бессрочно';
  if (days) {
    if (days >= 360) term = '1 год';
    else if (days < 28) term = formatDays(days);
    else {
      const months = Math.round(days / 30);
      term = `${months} ${months === 1 ? 'месяц' : months < 5 ? 'месяца' : 'месяцев'}`;
    }
  }
  return `${item.is_gift ? 'Подарок' : 'VPN'}, ${term}`;
}

function amount(item: PaymentHistory['items'][number]): string {
  const value =
    item.currency === 'XTR' ? `${item.amount_minor} ⭐` : formatRub(item.amount_minor);
  return item.status === 'refunded' ? `возврат ${value}` : value;
}

function Transactions() {
  const history = useQuery({
    queryKey: ['payment-history'],
    queryFn: () => apiGet('/payments/history', paymentHistorySchema),
  });
  const items = history.data?.items ?? [];
  if (history.isLoading) {
    return (
      <Card>
        <p className={styles.empty}>Загружаем…</p>
      </Card>
    );
  }
  if (items.length === 0) {
    return (
      <Card>
        <p className={styles.empty}>Платежей пока не было</p>
      </Card>
    );
  }
  return (
    <Card>
      {items.map((item) => (
        <Row
          key={item.id}
          icon={item.status === 'refunded' ? 'arrowUp' : item.is_gift ? 'gift' : 'wallet'}
          title={title(item)}
          subtitle={formatDate(item.paid_at)}
          value={
            item.status === 'refunded' ? (
              <span className={styles.refund}>{amount(item)}</span>
            ) : (
              amount(item)
            )
          }
        />
      ))}
    </Card>
  );
}

export function ProfilePage() {
  const navigate = useNavigate();
  const [sheet, setSheet] = useState(false);
  // Профиль — вкладка, своей кнопки «назад» у неё быть не должно. Но пока
  // открыта шторка, нажатие обязано закрывать её, а не сворачивать окно.
  useBackButton(() => setSheet(false), sheet);

  const { data, isLoading, error, refetch } = useProfile();
  const raffles = useRaffles();
  const wheel = useWheel();
  const activeRaffles = (raffles.data?.items ?? []).filter((r) => r.status === 'active');
  const myTickets = activeRaffles.reduce((sum, r) => sum + r.my_tickets, 0);

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      {data ? (
        <>
          <GroupCaption>Аккаунт</GroupCaption>
          <Card>
            <Row icon="user" title={data.first_name ?? 'Без имени'} subtitle="Имя" />
            {data.username ? (
              <Row icon="user" title={`@${data.username}`} subtitle="Username" />
            ) : null}
            <Row icon="shield" title={String(data.telegram_id)} subtitle="Telegram ID" />
            <Row icon="clock" title={formatDate(data.registered_at)} subtitle="В сервисе с" />
          </Card>

          {wheel.data && (wheel.data.spins_left > 0 || wheel.data.history.length > 0) ? (
            <Card>
              <Row
                icon="refresh"
                title="Рулетка"
                subtitle={
                  wheel.data.spins_left > 0
                    ? `${wheel.data.spins_left} ${spinsWord(wheel.data.spins_left)} — крутите`
                    : 'Мои призы'
                }
                value={wheel.data.spins_left > 0 ? String(wheel.data.spins_left) : undefined}
                onClick={() => void navigate('/wheel')}
              />
            </Card>
          ) : null}

          {activeRaffles.length > 0 ? (
            <Card>
              <Row
                icon="star"
                title="Конкурсы"
                subtitle={
                  myTickets > 0
                    ? `${myTickets} ${ticketWord(myTickets)} · ${activeRaffles[0]?.title ?? ''}`
                    : `Идёт: ${activeRaffles[0]?.title ?? ''}`
                }
                value={myTickets > 0 ? String(myTickets) : undefined}
                onClick={() => void navigate('/raffles')}
              />
            </Card>
          ) : null}

          {/* Разделы идут одной карточкой без заголовка: подпись над ней
              повторяла бы название единственной строки. */}
          <Card>
            <Row icon="wallet" title="История транзакций" onClick={() => setSheet(true)} />
            {data.referral_code ? (
              <Row
                icon="users"
                title="Реферальная программа"
                onClick={() => void navigate('/referral')}
              />
            ) : null}
            <Row
              icon="shield"
              title="Помощь"
              subtitle="VPN не работает? Поддержка"
              onClick={() => void navigate('/help')}
            />
          </Card>

          <Sheet open={sheet} title="История транзакций" onClose={() => setSheet(false)}>
            {sheet ? <Transactions /> : null}
          </Sheet>
        </>
      ) : null}
    </Screen>
  );
}
